/**
 * Strategy DNA API Service
 *
 * Provides access to Strategy DNA endpoints for bounce/break/fakeout analysis.
 * All endpoints require JWT Bearer token authentication.
 *
 * Backend Field Reference:
 * - avgMfePips, avgMaePips: Values in pips
 * - time_to_outcome_mins: Inside outcomeDetails JSON
 * - ADX Ranges: RANGING | WEAK_TREND | GOOD_TREND | STRONG_TREND
 * - Sessions: ASIA | EUROPE | EU_US_OVERLAP | US | CLOSED
 * - Trends: STRONG_UPTREND | MODERATE_UPTREND | NEUTRAL | MODERATE_DOWNTREND | STRONG_DOWNTREND | UNKNOWN
 *
 * @see docs/PLAN_StrategyDNA_F3_Implementation.md
 */

import axios from 'axios';
import { dataFeedURL } from '../config';

// Base URL for Strategy DNA endpoints
// DIRECT to DataFeed Server (port 5100) - NO proxy needed
// Strategy DNA endpoints are native to DataFeed (StrategyDnaController.cs)
// Endpoints are public (no auth required)
const STRATEGY_DNA_BASE_URL = `${dataFeedURL}/api/strategy-dna`;

// Request timeout (5 seconds)
const REQUEST_TIMEOUT = 5000;

// Performance warning threshold (ms)
const SLOW_REQUEST_THRESHOLD = 3000;

/**
 * Create axios instance with default config
 * Note: Strategy DNA endpoints are public - no auth header required
 */
const createAxiosInstance = () => {
    return axios.create({
        baseURL: STRATEGY_DNA_BASE_URL,
        timeout: REQUEST_TIMEOUT,
        headers: {
            'Content-Type': 'application/json'
        }
    });
};

/**
 * Log slow requests for performance monitoring
 */
const logPerformance = (endpoint, startTime) => {
    const duration = Date.now() - startTime;
    if (duration > SLOW_REQUEST_THRESHOLD) {
        console.warn(`[Strategy DNA API] Slow request: ${endpoint} took ${duration}ms`);
    }
    return duration;
};

/**
 * Handle API errors consistently
 */
const handleError = (error, endpoint) => {
    if (error.response) {
        console.error(`[Strategy DNA API] ${endpoint} failed:`, error.response.status, error.response.data);
        throw {
            status: error.response.status,
            message: error.response.data?.message || error.response.data?.error || 'Request failed',
            endpoint
        };
    } else if (error.request) {
        console.error(`[Strategy DNA API] ${endpoint} - No response:`, error.message);
        throw {
            status: 0,
            message: 'No response from server. Check if DataFeed Server is running.',
            endpoint
        };
    } else {
        console.error(`[Strategy DNA API] ${endpoint} - Request error:`, error.message);
        throw {
            status: -1,
            message: error.message,
            endpoint
        };
    }
};

/**
 * ADX Range enum values (from backend)
 */
export const ADX_RANGES = {
    RANGING: 'RANGING',           // ADX < 20
    WEAK_TREND: 'WEAK_TREND',     // ADX 20-25
    GOOD_TREND: 'GOOD_TREND',     // ADX 25-40
    STRONG_TREND: 'STRONG_TREND'  // ADX > 40
};

/**
 * Session enum values (from backend)
 */
export const SESSIONS = {
    ASIA: 'ASIA',
    EUROPE: 'EUROPE',
    EU_US_OVERLAP: 'EU_US_OVERLAP',
    US: 'US',
    CLOSED: 'CLOSED'
};

/**
 * Trend direction enum values (from backend)
 */
export const TREND_DIRECTIONS = {
    STRONG_UPTREND: 'STRONG_UPTREND',
    MODERATE_UPTREND: 'MODERATE_UPTREND',
    NEUTRAL: 'NEUTRAL',
    MODERATE_DOWNTREND: 'MODERATE_DOWNTREND',
    STRONG_DOWNTREND: 'STRONG_DOWNTREND',
    UNKNOWN: 'UNKNOWN'
};

/**
 * Level type enum values
 */
export const LEVEL_TYPES = {
    FIB: 'FIB',
    EMA: 'EMA',
    PIVOT: 'PIVOT'
};

/**
 * GET /api/strategy-dna/summary/{symbol}
 *
 * Returns overall stats and recent events for a symbol.
 *
 * @param {string} token - JWT authentication token
 * @param {string} symbol - Trading symbol (e.g., 'XAUUSD_ct')
 * @returns {Promise<Object>} Summary data with stats and recent events
 */
export const getDNASummary = async (token, symbol) => {
    const startTime = Date.now();
    const endpoint = `/summary/${symbol}`;

    try {
        const api = createAxiosInstance();
        const response = await api.get(endpoint);
        logPerformance(endpoint, startTime);
        return response.data;
    } catch (error) {
        handleError(error, endpoint);
    }
};

/**
 * GET /api/strategy-dna/level-stats/{symbol}
 *
 * Returns performance statistics by level/condition combinations.
 *
 * Query Parameters:
 * - levelType: FIB | EMA | PIVOT
 * - levelValue: 38.2% | 50% | 61.8% | etc.
 * - trendDirection: STRONG_UPTREND | MODERATE_UPTREND | NEUTRAL | MODERATE_DOWNTREND | STRONG_DOWNTREND
 * - adxRange: RANGING | WEAK_TREND | GOOD_TREND | STRONG_TREND
 * - session: ASIA | EUROPE | EU_US_OVERLAP | US | CLOSED
 * - minEvents: number (default 3)
 *
 * Response includes:
 * - stats[]: All matching level combinations
 * - topBounceLevels[]: Top 5 by bounce rate
 * - highFakeoutWarnings[]: Levels with >30% fakeout rate
 *
 * @param {string} token - JWT authentication token
 * @param {string} symbol - Trading symbol
 * @param {Object} filters - Optional query filters
 * @returns {Promise<Object>} Level statistics with arrays
 */
export const getLevelStats = async (token, symbol, filters = {}) => {
    const startTime = Date.now();
    const params = new URLSearchParams();

    // Build query string from filters
    if (filters.levelType) params.append('levelType', filters.levelType);
    if (filters.levelValue) params.append('levelValue', filters.levelValue);
    if (filters.trendDirection) params.append('trendDirection', filters.trendDirection);
    if (filters.adxRange) params.append('adxRange', filters.adxRange);
    if (filters.session) params.append('session', filters.session);
    if (filters.minEvents) params.append('minEvents', filters.minEvents.toString());
    if (filters.timeframe) params.append('timeframe', filters.timeframe);

    const queryString = params.toString();
    const endpoint = `/level-stats/${symbol}${queryString ? `?${queryString}` : ''}`;

    try {
        const api = createAxiosInstance(token);
        const response = await api.get(endpoint);
        logPerformance(endpoint, startTime);
        return response.data;
    } catch (error) {
        handleError(error, endpoint);
    }
};

/**
 * GET /api/strategy-dna/events/{symbol}
 *
 * Returns raw event list for drill-down analysis.
 *
 * @param {string} token - JWT authentication token
 * @param {string} symbol - Trading symbol
 * @param {Object} filters - Optional filters (levelType, outcome, limit, offset)
 * @returns {Promise<Object>} Event list with pagination
 */
export const getDNAEvents = async (token, symbol, filters = {}) => {
    const startTime = Date.now();
    const params = new URLSearchParams();

    if (filters.levelType) params.append('levelType', filters.levelType);
    if (filters.levelValue) params.append('levelValue', filters.levelValue);
    if (filters.outcome) params.append('outcome', filters.outcome);
    if (filters.limit) params.append('limit', filters.limit.toString());
    if (filters.offset) params.append('offset', filters.offset.toString());

    const queryString = params.toString();
    const endpoint = `/events/${symbol}${queryString ? `?${queryString}` : ''}`;

    try {
        const api = createAxiosInstance(token);
        const response = await api.get(endpoint);
        logPerformance(endpoint, startTime);
        return response.data;
    } catch (error) {
        handleError(error, endpoint);
    }
};

/**
 * Fetch all Strategy DNA data for a symbol in parallel
 *
 * Fetches summary and level-stats simultaneously for optimal performance.
 *
 * @param {string} token - JWT authentication token
 * @param {string} symbol - Trading symbol
 * @returns {Promise<Object>} Combined data from all endpoints
 */
export const fetchAllDNAData = async (token, symbol) => {
    const startTime = Date.now();

    try {
        const [summary, levelStats] = await Promise.all([
            getDNASummary(token, symbol),
            getLevelStats(token, symbol, { minEvents: 3 })
        ]);

        const duration = Date.now() - startTime;
        console.log(`[Strategy DNA API] fetchAllDNAData completed in ${duration}ms`);

        return {
            summary,
            levelStats,
            fetchedAt: new Date().toISOString()
        };
    } catch (error) {
        console.error('[Strategy DNA API] fetchAllDNAData failed:', error);
        throw error;
    }
};

/**
 * Get high-fakeout warnings for a symbol
 *
 * Convenience method that fetches level-stats with minEvents filter
 * and returns only the highFakeoutWarnings array.
 *
 * @param {string} token - JWT authentication token
 * @param {string} symbol - Trading symbol
 * @param {number} minEvents - Minimum events for statistical significance (default 3)
 * @returns {Promise<Array>} Array of high-fakeout level stats
 */
export const getFakeoutWarnings = async (token, symbol, minEvents = 3) => {
    const data = await getLevelStats(token, symbol, { minEvents });
    return data?.highFakeoutWarnings || [];
};

/**
 * GET /api/strategy-dna/symbols
 *
 * Returns list of symbols with Strategy DNA event counts.
 *
 * @returns {Promise<Object>} Symbols list with event counts
 */
export const getAvailableSymbols = async () => {
    const startTime = Date.now();
    const endpoint = '/symbols';

    try {
        const api = createAxiosInstance();
        const response = await api.get(endpoint);
        logPerformance(endpoint, startTime);
        return response.data;
    } catch (error) {
        handleError(error, endpoint);
    }
};

// Default export with all functions and enums
export default {
    // API functions
    getDNASummary,
    getLevelStats,
    getDNAEvents,
    fetchAllDNAData,
    getFakeoutWarnings,
    getAvailableSymbols,

    // Enums
    ADX_RANGES,
    SESSIONS,
    TREND_DIRECTIONS,
    LEVEL_TYPES
};
